import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Palette, Shirt, Sparkles } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import Img from "@/components/Img";

const VALUES = [
  { t: "Unhurried", d: "No rushing, no forced smiles. We let the good frames arrive." },
  { t: "Soft light", d: "Natural, flattering light is the heartbeat of every session." },
  { t: "For keeps", d: "Every gallery is designed to become prints, albums, and walls." },
];

const FUN_FACTS = [
  {
    icon: Palette,
    text: "Black is my favorite color—and yes, it absolutely counts as a color!",
  },
  {
    icon: Shirt,
    text: "Leggings and t-shirts? That's my unofficial uniform.",
  },
  {
    icon: Sparkles,
    text: "No matter how many times we've teamed up, I still get those pre-session butterflies. It's just my perfectionist side acting up!",
  },
];

export default function MeetJac() {
  return (
    <div className="pt-8 md:pt-12">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6">Meet the photographer</p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["Hi, I'm Jac"]}
        />
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <Reveal className="md:col-span-6" y={60}>
          <div className="relative pb-20 sm:pb-24 pr-10 sm:pr-20">
            <div className="absolute -left-5 -top-5 h-36 w-36 rounded-full bg-sagelight hidden md:block" />
            <Img
              src="/jac-portrait.jpg"
              alt="Jacqueline Claire, photographer"
              className="relative z-10 aspect-[3/4] rounded-tl-[6rem] group"
              imgClassName="group-hover:scale-105"
            />
            <div className="absolute bottom-0 right-0 z-20 w-40 sm:w-56 md:w-64 rotate-3 bg-white p-2.5 pb-7 shadow-[0_28px_60px_rgba(26,27,24,0.24)] transition-transform duration-500 ease-out hover:rotate-0">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-16 -rotate-2 rounded-sm bg-clay/60" />
              <Img src="/jac-portrait2.jpg" alt="Jac behind the camera" className="aspect-[3/4]" />
            </div>
          </div>
        </Reveal>
        <Reveal className="md:col-span-6 md:pt-10" delay={0.1}>
          <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-tight">
            I&rsquo;m Jacqueline, known to most as Jac!
          </h2>
          <div className="mt-8 space-y-5 text-inksoft leading-relaxed max-w-xl">
            <p>
              I&rsquo;m a proud mama of two — a 3-year-old girl and a 2-month-old boy — and a
              fortunate wife to my incredibly supportive hubby, Chris, who keeps me grounded.
            </p>
            <p>
              Photography has been my thing since 2008-ish, but I officially turned my passion
              into a career in 2020!
            </p>
            <p>
              My childhood was filled with the joy of capturing memories, thanks to my mom&rsquo;s
              constant snapping of our family adventures and special moments. I adored reliving
              those memories and quickly became obsessed with slideshows, scrapbooks, and covering
              my walls in photos. One day, I snatched my mom&rsquo;s camera and began capturing my
              own stories — starting with her garden and pretty landscapes. And as they say... the
              rest is history!
            </p>
            <p>
              With a camera in my hand, I get to tell stories, capture emotions, and freeze time in
              a truly magical way. My style is vibrant, bold, a little moody, and always authentic.
              Whether it&rsquo;s portraits, lifestyle sessions, or weddings, I aim to create genuine
              images that resonate with the people in them.
            </p>
            <p>
              Every session is a unique collaboration, and I&rsquo;m here to make the process as
              enjoyable and stress-free as possible while delivering images you&rsquo;ll treasure
              forever. Let&rsquo;s create something beautiful together!
            </p>
          </div>
          <p className="mt-10 font-serif italic text-3xl text-sage">— Jac</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.1}>
              <span className="font-serif text-sage/50 text-6xl leading-none">0{i + 1}</span>
              <h3 className="mt-4 font-serif text-3xl text-ink">{v.t}</h3>
              <p className="mt-3 text-inksoft leading-relaxed">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-sagelight py-24 md:py-32 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-14 md:mb-20 text-center">
            <p className="overline text-sagedark mb-5">A few fun facts</p>
            <h2 className="font-serif font-light text-ink text-5xl md:text-7xl leading-[0.95]">
              Getting to know me
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-12 items-center">
            {/* Scrapbook photo cluster — different sizes, rotated & overlapping */}
            <div className="relative mx-auto h-[420px] sm:h-[540px] w-full max-w-[540px]">
              {[
                { src: "/jac-1.jpg", cls: "left-0 top-10 w-40 sm:w-56 -rotate-6 z-10" },
                { src: "/jac-3.jpg", cls: "right-0 top-0 w-32 sm:w-44 rotate-6 z-20" },
                { src: "/jac-2.jpg", cls: "left-1/2 -translate-x-1/2 bottom-0 w-44 sm:w-60 -rotate-2 z-30" },
              ].map((p, i) => (
                <div
                  key={i}
                  className={`absolute ${p.cls} bg-white p-2.5 pb-8 shadow-[0_24px_50px_rgba(26,27,24,0.20)] transition-transform duration-500 ease-out hover:rotate-0 hover:z-40`}
                >
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-16 rotate-3 rounded-sm bg-clay/60" />
                  <Img src={p.src} alt={`Jacqueline Claire ${i + 1}`} className="aspect-[3/4]" />
                </div>
              ))}
            </div>

            {/* Fun facts as scattered notes */}
            <div className="space-y-6">
              {FUN_FACTS.map((f, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div
                    data-testid={`fun-fact-${i}`}
                    className={`flex items-start gap-5 rounded-md border border-ink/5 bg-cream p-6 md:p-7 shadow-[0_14px_36px_rgba(26,27,24,0.08)] transition-transform duration-500 hover:rotate-0 ${
                      i % 2 === 0 ? "-rotate-2 lg:-ml-4" : "rotate-2 lg:ml-6"
                    }`}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sage/15 text-sage">
                      <f.icon size={22} strokeWidth={1.5} />
                    </span>
                    <p className="font-serif italic text-xl md:text-2xl leading-snug text-ink">
                      {f.text}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-stone2 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-xl">
            Curious how a session feels?
          </h2>
          <Link
            to="/experience"
            data-testid="meetjac-experience-link"
            className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
          >
            The Experience
            <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
